/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl, { FormControlPropsSizeOverrides } from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { SxProps } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { menuProps } from "../../helpers";

interface ISelectOptions {
  text: string;
  value: string;
}

interface ISelectSmall {
  id: string;
  labelId: string;
  keyName?: string;
  inputLabel?: string;
  initialValue?: string | undefined;
  selectOptions: Array<ISelectOptions> | Array<string>;
  formControlSx?: SxProps;
  onChange: (arg: SelectChangeEvent) => void;
  helperText?: string | undefined;
  disabled?: boolean;
  displayNone?: boolean;
  size?: OverridableStringUnion<"small" | "medium", FormControlPropsSizeOverrides> | undefined

}

const SelectWithLabel: React.FC<ISelectSmall> = ({
  id,
  labelId,
  keyName,
  inputLabel,
  selectOptions,
  initialValue,
  formControlSx,
  onChange,
  helperText,
  disabled = false,
  displayNone = false,
  size = "medium",
}) => {
  const [value, setValue] = React.useState(initialValue);

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event);
    setValue(event.target.value);
  };
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <FormControl sx={{ minWidth: 120, ...formControlSx }} size={size}>
      <InputLabel id={id}>{inputLabel}</InputLabel>
      <Select
        sx={{
          "& .MuiSelect-icon": {
            color: "var(--muted-foreground)",
          }
        }}
        labelId={labelId}
        id={id}
        value={value}
        label={inputLabel}
        onChange={handleChange}
        name={keyName}
        MenuProps={menuProps}
        disabled={disabled}
        error={!!helperText}
        displayEmpty={displayNone}
      >
        {displayNone && (
          <MenuItem value="">None</MenuItem>
        )}
        {selectOptions.length > 0 && typeof selectOptions[0] === "object"
          ? selectOptions.map((item: any) => (
            <MenuItem key={item.value} value={item.value}>
              {item.text}
            </MenuItem>
          ))
          : selectOptions.map((item: any) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default SelectWithLabel;
